import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FaMoneyBillWave, FaUser, FaCoins, FaNotesMedical, FaUserCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { guestApi } from '../../api/guestList';
import apiClient from '../../api/index';
import { useAuthContext } from '../../context/AuthContext';
import { handleApiResponse } from '../global/ApiMessageModal/ApiMessageModal';

const OngezaMchangoModal = ({ show, onHide, onSuccess }) => {
  const { user } = useAuthContext();
  const [guests, setGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedGuestId, setSelectedGuestId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [receivedBy, setReceivedBy] = useState('');

  useEffect(() => {
    if (show) {
      loadGuests();
      setSelectedGuestId('');
      setAmount('');
      setDescription('');
      setReceivedBy(user?.full_name || 'Admin');
    }
  }, [show, user]);

  const loadGuests = async () => {
    setLoadingGuests(true);
    try {
      let rawData = [];

      // 1. Fetch from /guests/list endpoint
      try {
        const res = await apiClient.get('/guests/list');
        const payload = res?.data;
        if (Array.isArray(payload)) rawData = payload;
        else if (Array.isArray(payload?.data)) rawData = payload.data;
        else if (Array.isArray(payload?.guests)) rawData = payload.guests;
      } catch (e) {
        console.warn('/guests/list error, falling back to guestApi.getGuests():', e);
      }

      // 2. Fallback to guestApi.getGuests()
      if (!Array.isArray(rawData) || rawData.length === 0) {
        try {
          const res = await guestApi.getGuests();
          const payload = res?.data;
          if (Array.isArray(payload)) rawData = payload;
          else if (Array.isArray(payload?.data)) rawData = payload.data;
          else if (Array.isArray(payload?.guests)) rawData = payload.guests;
        } catch (e) {
          console.warn('guestApi.getGuests error:', e);
        }
      }

      if (!Array.isArray(rawData)) rawData = [];

      const formatted = rawData.map(g => ({
        id: g.id,
        name: g.name || g.full_name || 'Mgeni',
        phone_number: g.phone_number || g.mobile || g.phone || '',
        email: g.email || null,
        pledge: parseFloat(g.pledge || g.pledge_amount || 0),
        fund: parseFloat(g.fund || g.paid || g.amount_collected || 0),
        groups: g.groups || []
      }));

      setGuests(formatted);
    } catch (err) {
      console.error('Failed to load guests list:', err);
      setGuests([]);
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGuestId) {
      toast.warning('Tafadhali chagua mchangiaji / mgeni');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.warning('Tafadhali ingiza kiasi sahihi cha mchango');
      return;
    }

    const selectedGuest = guests.find(g => String(g.id) === String(selectedGuestId));
    if (!selectedGuest) {
      toast.error('Mgeni aliyechaguliwa hajapatikana');
      return;
    }

    setSubmitting(true);
    try {
      const groupIds = (selectedGuest.groups || []).map(g => g.id || g.value || g);
      const firstGroupId = groupIds.length > 0 ? groupIds[0] : null;

      const payload = {
        name: String(selectedGuest.name).trim(),
        phone_number: selectedGuest.phone_number || '',
        email: selectedGuest.email || null,
        pledge: parseFloat(selectedGuest.pledge) || 0,
        fund: parseFloat(amount), // Sends the fund contribution amount to update
        groups: groupIds,
        group_ids: groupIds,
        group_id: firstGroupId,
        guest_list_group_id: firstGroupId
      };

      const response = await guestApi.updateGuest(selectedGuest.id, payload);
      if (handleApiResponse(response)) {
        toast.success('Mchango umerekodiwa kikamilifu!');
        onHide();
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error('Error updating guest fund:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold d-flex align-items-center gap-2" style={{ color: '#4169e1' }}>
          <div className="p-2 rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center">
            <FaMoneyBillWave size={22} />
          </div>
          Rekodi Mchango Mpya
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          {/* Mchangiaji Guest Select */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FaUser className="me-2 text-muted" />
              Mchangiaji / Mgeni <span className="text-danger">*</span>
            </Form.Label>
            {loadingGuests ? (
              <div className="d-flex align-items-center gap-2 py-2 text-muted small">
                <Spinner animation="border" size="sm" /> Inapakia orodha ya wageni...
              </div>
            ) : (
              <Form.Select
                value={selectedGuestId}
                onChange={(e) => setSelectedGuestId(e.target.value)}
                required
                className="rounded-3 shadow-none border py-2"
                style={{ fontSize: '0.9rem' }}
              >
                <option value="">-- Chagua Mchangiaji / Mgeni --</option>
                {guests.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name} {g.pledge > 0 ? `- Ahadi: TZS ${g.pledge.toLocaleString()}` : ''} {g.fund > 0 ? `(Mchango: TZS ${g.fund.toLocaleString()})` : ''}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          {/* Kiasi Amount */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FaCoins className="me-2 text-muted" />
              Kiasi Cha Mchango (TZS) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              placeholder="Weka kiasi cha mchango (mfano: 50000)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="rounded-3 shadow-none border py-2"
            />
          </Form.Group>

          {/* Maelezo Description */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FaNotesMedical className="me-2 text-muted" />
              Maelezo ya Mchango (Si Lazima)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Maelezo ya ziada kuhusu mchango (mfano: Njia ya malipo, Benki, n.k.)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-3 shadow-none border"
            />
          </Form.Group>

          {/* Aliyepokea Received By */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FaUserCheck className="me-2 text-muted" />
              Mhifadhi / Aliyepokea
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Jina la aliyepokea mchango"
              value={receivedBy}
              onChange={(e) => setReceivedBy(e.target.value)}
              className="rounded-3 shadow-none border py-2"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 px-4 pb-4">
          <Button variant="light" onClick={onHide} className="rounded-pill px-4 fw-medium border">
            Ghairi
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            className="rounded-pill px-4 fw-semibold shadow-sm border-0"
            style={{ backgroundColor: '#4169e1' }}
          >
            {submitting ? (
              <><Spinner animation="border" size="sm" className="me-2" />Inahifadhi...</>
            ) : (
              'Rekodi Mchango'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default OngezaMchangoModal;
